import * as React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Trash2, KeyRound, Webhook, Plus, Play, Check, X, Boxes, ExternalLink, Mail, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * DevelopersSection — API keys + Webhooks + reference docs.
 * Rendered inside Settings when the caller has `company.manage`.
 */
export default function DevelopersSection() {
  const { companyId } = useParams();
  const [keys, setKeys] = React.useState([]);
  const [scopes, setScopes] = React.useState([]);
  const [webhooks, setWebhooks] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [creatingKey, setCreatingKey] = React.useState(false);
  const [newKeyLabel, setNewKeyLabel] = React.useState("");
  const [newKeyScopes, setNewKeyScopes] = React.useState({});
  const [plaintext, setPlaintext] = React.useState(null);
  const [wLabel, setWLabel] = React.useState("");
  const [wUrl, setWUrl] = React.useState("");
  const [wEvents, setWEvents] = React.useState({});
  const [creatingW, setCreatingW] = React.useState(false);
  const [freshSecret, setFreshSecret] = React.useState(null);
  const [deliveries, setDeliveries] = React.useState({});

  const load = React.useCallback(async () => {
    try {
      const [{ data: kd }, { data: wd }] = await Promise.all([
        api.get(`/companies/${companyId}/api-keys`),
        api.get(`/companies/${companyId}/webhooks`),
      ]);
      setKeys(kd.api_keys || []);
      setScopes(kd.scopes || []);
      setWebhooks(wd.webhooks || []);
      setEvents(wd.events || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed");
    }
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);

  async function createKey(e) {
    e.preventDefault();
    if (!newKeyLabel.trim() || creatingKey) return;
    setCreatingKey(true);
    try {
      const scopeList = Object.entries(newKeyScopes).filter(([, on]) => on).map(([s]) => s);
      const { data } = await api.post(`/companies/${companyId}/api-keys`, {
        label: newKeyLabel.trim(), scopes: scopeList,
      });
      setPlaintext(data.plaintext);
      toast.success("API key created — copy it now");
      setNewKeyLabel(""); setNewKeyScopes({});
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Create failed");
    } finally { setCreatingKey(false); }
  }

  async function revokeKey(kid) {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    try {
      await api.delete(`/companies/${companyId}/api-keys/${kid}`);
      toast.success("Revoked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  async function createWebhook(e) {
    e.preventDefault();
    if (!wUrl.trim() || creatingW) return;
    setCreatingW(true);
    try {
      const eventList = Object.entries(wEvents).filter(([, on]) => on).map(([s]) => s);
      const { data } = await api.post(`/companies/${companyId}/webhooks`, {
        url: wUrl.trim(), label: wLabel.trim(), events: eventList,
      });
      setFreshSecret({ id: data.webhook.id, secret: data.secret });
      toast.success("Webhook created — copy the secret now");
      setWUrl(""); setWLabel(""); setWEvents({});
      await load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Create failed");
    } finally { setCreatingW(false); }
  }

  async function deleteWebhook(wid) {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await api.delete(`/companies/${companyId}/webhooks/${wid}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  async function testFire(wid) {
    try {
      await api.post(`/companies/${companyId}/webhooks/${wid}/test-fire`, {
        event: "webhook.test",
      });
      toast.success("Test sent");
      loadDeliveries(wid);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }

  async function loadDeliveries(wid) {
    try {
      const { data } = await api.get(`/companies/${companyId}/webhooks/${wid}/deliveries`);
      setDeliveries((prev) => ({ ...prev, [wid]: data.deliveries || [] }));
    } catch { /* noop */ }
  }

  return (
    <div className="space-y-6" data-testid="developers-section">
      {/* API keys */}
      <section className="card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Developers</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
              <KeyRound size={14} /> API keys
            </div>
          </div>
        </div>
        <div className="card-panel-body space-y-4">
          <div className="text-[12px] text-brand-muted leading-relaxed">
            Keys authenticate <span className="mono-num">/api/v1/*</span> calls via the
            <span className="mono-num"> X-API-Key </span> header. Rate-limited to 60/min
            per key. Every call is audited with the key id as actor.
          </div>
          <form onSubmit={createKey} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-start" data-testid="new-api-key-form">
            <div className="flex flex-col gap-2">
              <input className="field-input" placeholder="Label (e.g. Booking widget)"
                     value={newKeyLabel} onChange={(e)=>setNewKeyLabel(e.target.value)}
                     data-testid="new-api-key-label" />
              <div className="flex flex-wrap gap-1.5">
                {scopes.map((s) => (
                  <label key={s} className={`chip cursor-pointer ${newKeyScopes[s] ? "!bg-brand-violet !text-white !border-brand-violet" : ""}`}
                         data-testid={`api-scope-${s}`}>
                    <input type="checkbox" className="hidden" checked={!!newKeyScopes[s]}
                           onChange={()=>setNewKeyScopes((p)=>({...p, [s]: !p[s]}))} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" className="btn-violet" disabled={!newKeyLabel.trim() || creatingKey}
                    data-testid="new-api-key-submit">
              <Plus size={13} /> {creatingKey ? "…" : "Create key"}
            </button>
          </form>

          {plaintext && (
            <div className="rounded-md border border-brand-violet/40 bg-brand-violet/5 p-3" data-testid="new-api-key-plaintext">
              <div className="text-[12px] font-semibold text-brand-violet">Copy this key now — it won't be shown again.</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="mono-num text-[12px] bg-card border border-brand-rule rounded px-2 py-1 flex-1 break-all">
                  {plaintext}
                </code>
                <button type="button" className="btn-outline" onClick={() => { navigator.clipboard.writeText(plaintext); toast.success("Copied"); }}
                        data-testid="copy-api-key">
                  <Copy size={12} /> Copy
                </button>
                <button type="button" className="btn-ghost" onClick={()=>setPlaintext(null)} data-testid="dismiss-api-key">
                  <X size={12} />
                </button>
              </div>
            </div>
          )}

          {/* Existing keys */}
          <div className="fable-table">
            <table>
              <thead>
                <tr>
                  <th>Label</th><th>Prefix</th><th>Scopes</th><th>Created</th><th>Last used</th><th>Calls</th><th></th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-brand-muted py-4">No keys yet.</td></tr>
                )}
                {keys.map((k) => (
                  <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                    <td className="font-semibold text-brand-ink">{k.label}</td>
                    <td className="mono-num">{k.prefix}…</td>
                    <td>{(k.scopes || []).join(", ") || <span className="text-brand-muted">—</span>}</td>
                    <td className="mono-num text-brand-muted">{k.created_at?.slice(0,10)}</td>
                    <td className="mono-num text-brand-muted">{k.last_used_at?.slice(0,16) || "—"}</td>
                    <td className="mono-num">{k.call_count || 0}</td>
                    <td className="text-right">
                      <button className="btn-ghost text-brand-coral" onClick={()=>revokeKey(k.id)}
                              data-testid={`revoke-api-key-${k.id}`}>
                        <Trash2 size={12} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Webhooks */}
      <section className="card-panel">
        <div className="card-panel-head">
          <div>
            <div className="micro-label">Developers</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
              <Webhook size={14} /> Webhooks
            </div>
          </div>
        </div>
        <div className="card-panel-body space-y-4">
          <div className="text-[12px] text-brand-muted leading-relaxed">
            HMAC-signed JSON POST. Header <span className="mono-num">X-Fable-Signature: sha256=&lt;hex&gt;</span>
            using the endpoint's secret. Up to 3 retries with exponential backoff (1s → 4s → 16s).
          </div>
          <form onSubmit={createWebhook} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-start" data-testid="new-webhook-form">
            <input className="field-input" placeholder="Label (optional)"
                   value={wLabel} onChange={(e)=>setWLabel(e.target.value)} />
            <div className="flex flex-col gap-2">
              <input className="field-input" placeholder="https://your-endpoint.example.com/webhooks"
                     value={wUrl} onChange={(e)=>setWUrl(e.target.value)} data-testid="new-webhook-url" />
              <div className="flex flex-wrap gap-1.5">
                {events.map((e) => (
                  <label key={e} className={`chip cursor-pointer ${wEvents[e] ? "!bg-brand-violet !text-white !border-brand-violet" : ""}`}
                         data-testid={`webhook-event-${e}`}>
                    <input type="checkbox" className="hidden" checked={!!wEvents[e]}
                           onChange={()=>setWEvents((p)=>({...p, [e]: !p[e]}))} />
                    {e}
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" className="btn-violet" disabled={!wUrl.trim() || creatingW}
                    data-testid="new-webhook-submit">
              <Plus size={13} /> {creatingW ? "…" : "Create"}
            </button>
          </form>

          {freshSecret && (
            <div className="rounded-md border border-brand-violet/40 bg-brand-violet/5 p-3">
              <div className="text-[12px] font-semibold text-brand-violet">Copy this secret now — it won't be shown again.</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="mono-num text-[12px] bg-card border border-brand-rule rounded px-2 py-1 flex-1 break-all">
                  {freshSecret.secret}
                </code>
                <button type="button" className="btn-outline"
                        onClick={()=>{ navigator.clipboard.writeText(freshSecret.secret); toast.success("Copied"); }}>
                  <Copy size={12} /> Copy
                </button>
                <button type="button" className="btn-ghost" onClick={()=>setFreshSecret(null)}><X size={12} /></button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {webhooks.length === 0 && (
              <div className="text-center text-brand-muted py-4 text-[12px]">No webhooks yet.</div>
            )}
            {webhooks.map((w) => (
              <div key={w.id} className="rounded-md border border-brand-rule bg-card p-3" data-testid={`webhook-row-${w.id}`}>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-brand-ink">{w.label}</div>
                    <div className="text-[11.5px] text-brand-muted break-all mono-num">{w.url}</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(w.events || []).map((e) => (
                        <span key={e} className="chip mono-num text-[10px]">{e}</span>
                      ))}
                    </div>
                    {w.last_delivery_at && (
                      <div className="mt-1 text-[10.5px] text-brand-muted">
                        Last: <span className={w.last_delivery_status === "delivered" ? "text-emerald-600" : "text-brand-coral"}>
                          {w.last_delivery_status}
                        </span> · <span className="mono-num">{w.last_delivery_at?.slice(0,16)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <button className="btn-outline" onClick={()=>testFire(w.id)} data-testid={`test-fire-${w.id}`}>
                      <Play size={12} /> Test fire
                    </button>
                    <button className="btn-ghost" onClick={()=>loadDeliveries(w.id)} data-testid={`load-deliveries-${w.id}`}>
                      Deliveries
                    </button>
                    <button className="btn-ghost text-brand-coral" onClick={()=>deleteWebhook(w.id)}
                            data-testid={`delete-webhook-${w.id}`}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
                {deliveries[w.id] && (
                  <div className="mt-3 fable-table" data-testid={`deliveries-${w.id}`}>
                    <table>
                      <thead>
                        <tr><th>Event</th><th>Status</th><th>HTTP</th><th>Attempts</th><th>At</th></tr>
                      </thead>
                      <tbody>
                        {deliveries[w.id].length === 0 && (
                          <tr><td colSpan={5} className="text-center text-brand-muted py-2">No deliveries yet.</td></tr>
                        )}
                        {deliveries[w.id].map((d) => (
                          <tr key={d.id}>
                            <td className="mono-num text-[11px]">{d.event}</td>
                            <td>
                              <span className={
                                d.status === "delivered" ? "chip !bg-emerald-50 !text-emerald-700 !border-emerald-200"
                                : d.status === "failed"  ? "chip !bg-rose-50 !text-rose-700 !border-rose-200"
                                : "chip"
                              }>{d.status}</span>
                            </td>
                            <td className="mono-num">{d.response_code || "—"}</td>
                            <td className="mono-num">{d.attempt}</td>
                            <td className="mono-num text-brand-muted">{d.updated_at?.slice(11,19)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking widget — Phase 8.5 */}
      <WidgetsSection scopes={scopes} keys={keys} />

      {/* Email outbox — Phase 16.3 */}
      <OutboxSection />

      {/* Reference */}
      <section className="card-panel" data-testid="developers-docs">        <div className="card-panel-head">
          <div>
            <div className="micro-label">Developers</div>
            <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Reference</div>
          </div>
        </div>
        <div className="card-panel-body space-y-4 text-[12.5px] leading-relaxed">
          <div>
            <div className="micro-label">API surface (v1)</div>
            <pre className="mono-num text-[11.5px] bg-brand-canvas border border-brand-rule rounded p-3 overflow-x-auto"
                 style={{ whiteSpace: "pre-wrap" }}>
{`GET  /api/v1/services           X-API-Key + services.read
GET  /api/v1/shifts             X-API-Key + rotas.read
GET  /api/v1/bookings           X-API-Key + bookings.read
POST /api/v1/bookings           X-API-Key + bookings.write`}</pre>
          </div>
          <div>
            <div className="micro-label">Verify a webhook signature (Python)</div>
            <pre className="mono-num text-[11.5px] bg-brand-canvas border border-brand-rule rounded p-3 overflow-x-auto"
                 style={{ whiteSpace: "pre-wrap" }}>
{`import hmac, hashlib
sig = "sha256=" + hmac.new(secret.encode(),
    body_bytes, hashlib.sha256).hexdigest()
assert hmac.compare_digest(sig, request.headers["X-Fable-Signature"])`}</pre>
          </div>
          <div>
            <div className="micro-label">Curl — create a booking</div>
            <pre className="mono-num text-[11.5px] bg-brand-canvas border border-brand-rule rounded p-3 overflow-x-auto"
                 style={{ whiteSpace: "pre-wrap" }}>
{`curl -X POST "$API/api/v1/bookings" \\
  -H "X-API-Key: sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "site_id": "<uuid>", "service_id": "<uuid>",
    "customer": {"name": "Jane Doe"},
    "starts_at": "2026-09-05T18:00:00+00:00"
  }'`}</pre>
          </div>
        </div>
      </section>
    </div>
  );
}

function WidgetsSection({ scopes, keys }) {
  const { companyId } = useParams();
  const [widgets, setWidgets] = React.useState([]);
  const [label, setLabel] = React.useState("");
  const [keyId, setKeyId] = React.useState("");
  const [color, setColor] = React.useState("#6E3CFA");
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/widgets`);
      setWidgets(data.widgets || []);
    } catch { /* noop */ }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  // Only allow keys with the right scopes
  const eligibleKeys = (keys || []).filter((k) =>
    (k.scopes || []).includes("services.read") && (k.scopes || []).includes("bookings.write")
  );

  async function create(e) {
    e.preventDefault();
    if (!label.trim() || !keyId || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/widgets`, {
        label: label.trim(), api_key_id: keyId, color, site_ids: [],
      });
      toast.success("Created");
      setLabel(""); setKeyId("");
      load();
    } catch (e2) { toast.error(formatApiErrorDetail(e2?.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  async function del(wid) {
    if (!window.confirm("Delete this widget? Existing embeds will stop working.")) return;
    try {
      await api.delete(`/companies/${companyId}/widgets/${wid}`);
      toast.success("Removed"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  }
  function snippet(w) {
    const url = `${window.location.origin}/widget/${w.id}`;
    return `<iframe src="${url}" style="width:100%;max-width:440px;height:720px;border:0;border-radius:16px;background:transparent" allow="clipboard-write"></iframe>`;
  }

  return (
    <section className="card-panel" data-testid="widgets-section">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Developers</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
            <Boxes size={14} /> Booking widget
          </div>
        </div>
      </div>
      <div className="card-panel-body space-y-4">
        <div className="text-[12px] text-brand-muted leading-relaxed">
          A copy-paste iframe your customers can drop on any website. Backed by
          an API key you already own — revoke the key to kill the widget. Rate-
          limited to 30 booking attempts / minute per widget. Bookings land as
          <span className="mono-num"> source=widget</span> and start
          <span className="mono-num"> pending</span>, following the same
          confirm/complete flow as any other booking.
        </div>
        {eligibleKeys.length === 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">
            You need an API key with <span className="mono-num">services.read</span> +
            <span className="mono-num"> bookings.write</span> scopes to power a widget.
            Create one in the section above.
          </div>
        )}
        {eligibleKeys.length > 0 && (
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto_auto] gap-2 items-start" data-testid="new-widget-form">
            <input className="field-input" placeholder="Label (e.g. Marketing site)"
                   value={label} onChange={(e)=>setLabel(e.target.value)}
                   data-testid="new-widget-label" />
            <select className="field-input" value={keyId} onChange={(e)=>setKeyId(e.target.value)}
                    data-testid="new-widget-key">
              <option value="">Pick an API key…</option>
              {eligibleKeys.map((k) => (
                <option key={k.id} value={k.id}>{k.label} · {k.prefix}…</option>
              ))}
            </select>
            <input type="color" className="field-input !w-11 !px-0" value={color}
                   onChange={(e)=>setColor(e.target.value)}
                   data-testid="new-widget-color" />
            <button type="submit" className="btn-violet" disabled={busy || !label.trim() || !keyId}
                    data-testid="new-widget-submit">
              <Plus size={13} /> Create widget
            </button>
          </form>
        )}
        <div className="space-y-3">
          {widgets.length === 0 && (
            <div className="text-center py-4 text-[12px] text-brand-muted">No widgets yet.</div>
          )}
          {widgets.map((w) => (
            <div key={w.id} className="rounded-md border border-brand-rule bg-card p-3" data-testid={`widget-row-${w.id}`}>
              <div className="flex items-start gap-2">
                <div className="h-9 w-9 rounded-md shrink-0" style={{ background: w.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-brand-ink">{w.label}</div>
                  <div className="mono-num text-[10.5px] text-brand-muted break-all">
                    key: {w.api_key_id.slice(0, 8)}… · created {w.created_at?.slice(0,10)}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <a href={`/widget/${w.id}`} target="_blank" rel="noreferrer"
                       className="btn-outline !h-7" data-testid={`widget-preview-${w.id}`}>
                      <ExternalLink size={12} /> Preview
                    </a>
                    <button type="button" className="btn-outline !h-7"
                            onClick={() => { navigator.clipboard.writeText(snippet(w)); toast.success("Copied embed snippet"); }}
                            data-testid={`widget-copy-snippet-${w.id}`}>
                      <Copy size={12} /> Copy embed snippet
                    </button>
                    <button type="button" className="btn-ghost text-brand-coral"
                            onClick={() => del(w.id)} data-testid={`widget-delete-${w.id}`}>
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                  <details className="mt-2">
                    <summary className="text-[11px] text-brand-muted cursor-pointer">
                      Embed snippet
                    </summary>
                    <pre className="mt-1 mono-num text-[11px] bg-brand-canvas border border-brand-rule rounded p-2 overflow-x-auto whitespace-pre-wrap">
{snippet(w)}
                    </pre>
                  </details>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}



// ─────────────────────────────────────────────────────────────
// OutboxSection — Phase 16.3
// Shows every reset / notification email captured by the dev outbox,
// plus the live provider status.  Owners only (require: company.manage).
// ─────────────────────────────────────────────────────────────
function OutboxSection() {
  const { companyId } = useParams();
  const [data, setData] = React.useState(null);
  const [openId, setOpenId] = React.useState(null);
  const [err, setErr] = React.useState("");

  const load = React.useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/companies/${companyId}/dev/outbox`);
      setData(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Load failed");
    }
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);

  const isLive = !!data?.provider?.live;
  const providerLabel = data?.provider?.provider || "outbox";

  return (
    <section className="card-panel" data-testid="outbox-section">
      <div className="card-panel-head">
        <div>
          <div className="micro-label">Developers</div>
          <div className="mt-0.5 text-[14px] font-semibold text-brand-ink flex items-center gap-2">
            <Mail size={14} /> Email outbox
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`chip mono-num text-[10px] ${isLive ? "chip-emerald" : ""}`}
                data-testid="outbox-provider-status">
            <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${isLive ? "bg-emerald-600" : "bg-amber-500"}`} />
            {isLive ? "Live · Resend" : `Dev · ${providerLabel}`}
          </span>
          <button className="btn-outline !h-7" onClick={load} data-testid="outbox-refresh">Refresh</button>
        </div>
      </div>
      <div className="card-panel-body space-y-4">
        <div className="text-[12px] text-brand-muted leading-relaxed">
          {isLive ? (
            <>Live email delivery via Resend. The outbox still keeps a lightweight
            audit copy of every send (without HTML body).</>
          ) : (
            <>{data?.notice || "Set RESEND_API_KEY in the backend .env then restart to flip to live delivery."}</>
          )}
        </div>
        {err && <div className="text-[12px] text-brand-coral" data-testid="outbox-error">{err}</div>}
        <div className="fable-table">
          <table>
            <thead>
              <tr>
                <th>Sent</th><th>To</th><th>Subject</th><th>Tag</th><th>Provider</th><th></th>
              </tr>
            </thead>
            <tbody>
              {!data && (
                <tr><td colSpan={6} className="text-center text-brand-muted py-3">Loading…</td></tr>
              )}
              {data && data.count === 0 && (
                <tr><td colSpan={6} className="text-center text-brand-muted py-3">
                  No emails captured yet. Trigger a password reset from the login screen to see one land here.
                </td></tr>
              )}
              {data && data.outbox.map((row) => (
                <React.Fragment key={row.id}>
                  <tr data-testid={`outbox-row-${row.id}`}>
                    <td className="mono-num text-[11px] text-brand-muted whitespace-nowrap">
                      {row.sent_at?.slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="mono-num text-[11.5px]">{row.to}</td>
                    <td className="text-[12px] text-brand-ink">{row.subject}</td>
                    <td><span className="chip mono-num text-[10px]">{row.tag || "—"}</span></td>
                    <td>
                      <span className={`chip mono-num text-[10px] ${
                        row.provider === "resend" ? "chip-emerald"
                        : row.provider === "outbox" ? "" : "chip-violet"
                      }`}>{row.provider} · {row.status?.toLowerCase() || "-"}</span>
                    </td>
                    <td className="text-right">
                      {row.html ? (
                        <button className="btn-ghost !h-7 !px-2"
                                onClick={() => setOpenId(openId === row.id ? null : row.id)}
                                data-testid={`outbox-toggle-${row.id}`}>
                          {openId === row.id ? "Hide" : "Preview"}
                        </button>
                      ) : <span className="text-[11px] text-brand-muted mono-num">no body</span>}
                    </td>
                  </tr>
                  {openId === row.id && row.html && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <div className="border-t border-brand-rule bg-brand-canvas p-3"
                             data-testid={`outbox-preview-${row.id}`}>
                          <div className="text-[10.5px] text-brand-muted mb-2 mono-num">
                            From: {row.from_email} · To: {row.to}
                          </div>
                          <iframe
                            title={`outbox-${row.id}`}
                            srcDoc={row.html}
                            sandbox=""
                            className="w-full min-h-[240px] rounded border border-brand-rule bg-white"
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
